import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { recordObservedMessage, sendMessage } from '../../api/dialogs';
import { ApiError } from '../../api/client';
import type { DialogDto } from '../../api/types';
import { Button } from '../ui/button';
import { useToast } from '../ui/toast';

export function Composer({ dialog }: { dialog: DialogDto }) {
  const [text, setText] = useState('');
  const queryClient = useQueryClient();
  const toast = useToast();
  const disabled = dialog.channelType !== 'vk_group' || dialog.state === 'CLOSED';

  const send = useMutation({
    mutationFn: () => sendMessage(dialog.id, text.trim()),
    onSuccess: () => {
      setText('');
      void queryClient.invalidateQueries({ queryKey: ['dialogs', 'messages', dialog.id] });
      void queryClient.invalidateQueries({ queryKey: ['dialogs', 'detail', dialog.id] });
    },
    onError: (err) => toast.push(err instanceof ApiError ? err.message : 'Не удалось отправить сообщение'),
  });

  const recordObserved = useMutation({
    mutationFn: () =>
      recordObservedMessage({
        dialog_id: dialog.id,
        text: text.trim(),
        sent_at: new Date().toISOString(),
      }),
    onSuccess: () => {
      setText('');
      toast.push('Отправка зафиксирована', 'success');
      void queryClient.invalidateQueries({ queryKey: ['dialogs', 'messages', dialog.id] });
      void queryClient.invalidateQueries({ queryKey: ['dialogs', 'detail', dialog.id] });
    },
    onError: (err) => toast.push(err instanceof ApiError ? err.message : 'Не удалось зафиксировать отправку'),
  });

  if (dialog.channelType === 'vk_personal') {
    return (
      <div className="border-t border-border p-3">
        <p className="mb-2 text-sm text-muted-fg">Отправьте сообщение из VK, затем зафиксируйте отправку здесь.</p>
        <div className="flex gap-2">
          <textarea
            className="h-16 flex-1 resize-none rounded border border-border bg-card px-3 py-2 text-sm"
            placeholder="Текст сообщения, отправленного в VK…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Button
            variant="secondary"
            disabled={!text.trim() || recordObserved.isPending}
            onClick={() => recordObserved.mutate()}
          >
            Зафиксировать отправку
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="flex gap-2 border-t border-border p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) send.mutate();
      }}
    >
      <textarea
        className="h-16 flex-1 resize-none rounded border border-border bg-card px-3 py-2 text-sm disabled:opacity-50"
        placeholder="Сообщение клиенту…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={disabled}
      />
      <Button type="submit" disabled={disabled || !text.trim() || send.isPending}>
        Отправить
      </Button>
    </form>
  );
}
